import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Send, Eye, MessageSquare, MousePointer,
  Zap, Users, Mail, CheckCircle,
  TrendingUp, BarChart3, Loader2, Clock, RefreshCw, ArrowUpRight, ArrowDownRight,
  Building2, Zap as ZapIcon, AlertCircle,
  Smartphone, Globe, UserPlus, Repeat, Layers, ExternalLink, PoundSterling
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CallStatsPanel from '../components/CallStatsPanel';
import { VisitorSegmentsCard } from '../components/VisitorSegmentsCard';
import { CareSettingsCard } from '../components/CareSettingsCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-blue-400', bgColor = 'bg-blue-500/10', onClick }) => (
  <Card
    className={`bg-slate-900/50 border-slate-800 ${onClick ? 'cursor-pointer hover:border-emerald-600/60 hover:bg-slate-900 transition-colors group' : ''}`}
    onClick={onClick}
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${bgColor} rounded-lg`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-mono text-white flex items-center gap-1.5">{value}
            {onClick && <ExternalLink className="w-3.5 h-3.5 text-slate-600 group-hover:text-emerald-400 transition-colors" />}
          </p>
          <p className="text-xs text-slate-500">{label}</p>
          {sub && <p className="text-[11px] text-slate-600">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const DrilldownDialog = ({ drill, days, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!drill) { setData(null); return; }
    let active = true;
    setLoading(true);
    setData(null);
    axios.get(`${API}/analytics/drilldown`, { params: { kind: drill.kind, days } })
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) toast.error('Failed to load records'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [drill, days]);

  const openRecord = (r) => {
    if (r.link_type === 'company') { onClose(); navigate(`/admin/companies?open=${r.link_id}`); }
    else if (r.link_type === 'contact') { onClose(); navigate(`/admin/contacts?open=${r.link_id}`); }
  };

  const rows = data?.rows || [];
  const hasValue = rows.some((r) => r.value_gbp != null && r.value_gbp > 0);
  const fmtGbp = (n) => '£' + Number(n || 0).toLocaleString();
  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 sm:max-w-6xl sm:w-[92vw] max-h-[85vh] overflow-hidden flex flex-col" data-testid="drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 flex-wrap">
            <Layers className="w-5 h-5 text-emerald-400" />
            {drill?.title || 'Records'}
            {data && <Badge variant="outline" className="border-emerald-700 text-emerald-400 ml-1">{data.count}{data.count >= 500 ? '+' : ''}</Badge>}
            {hasValue && data?.total_value > 0 && (
              <Badge variant="outline" className="border-amber-600 text-amber-400" data-testid="drilldown-total-value">
                {fmtGbp(data.total_value)}/yr total
              </Badge>
            )}
            <span className="text-xs text-slate-500 font-normal ml-1">last {days} days</span>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto overflow-x-auto -mx-6 px-6" data-testid="drilldown-body">
          {loading ? (
            <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500 inline" /></div>
          ) : rows.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-500" data-testid="drilldown-empty">No records found for this stat.</p>
          ) : (
            <table className="w-full text-sm min-w-[520px]">
              <thead className="sticky top-0 bg-slate-950">
                <tr className="border-b border-slate-800 text-xs text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-2 pr-3 font-medium">Name</th>
                  <th className="text-left py-2 px-3 font-medium">Status</th>
                  {hasValue && <th className="text-right py-2 px-3 font-medium">Value / yr</th>}
                  <th className="text-left py-2 px-3 font-medium">Website / Email</th>
                  <th className="text-right py-2 pl-3 font-medium">Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`drilldown-row-${i}`}>
                    <td className="py-2.5 pr-3">
                      <div className="text-white font-medium truncate max-w-[340px]">{r.name}</div>
                      {r.sub && <div className="text-xs text-slate-500 truncate max-w-[340px]">{r.sub}</div>}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-300 capitalize">{r.status || '-'}</span>
                    </td>
                    {hasValue && (
                      <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap" data-testid={`drilldown-value-${i}`}>
                        {r.value_gbp ? <span className="text-amber-400">{fmtGbp(r.value_gbp)}</span> : <span className="text-slate-600">-</span>}
                      </td>
                    )}
                    <td className="py-2.5 px-3 max-w-[360px] truncate">
                      {r.website ? (
                        <a href={r.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                          className="text-emerald-400 hover:underline" data-testid={`drilldown-website-${i}`}>{r.website.replace(/^https?:\/\//, '')}</a>
                      ) : r.email ? (
                        <span className="text-slate-400">{r.email}</span>
                      ) : <span className="text-slate-600">-</span>}
                    </td>
                    <td className="py-2.5 pl-3 text-right">
                      {r.link_type ? (
                        <Button size="sm" variant="ghost" onClick={() => openRecord(r)}
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20 h-7"
                          data-testid={`drilldown-open-${i}`}>
                          Open <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      ) : <span className="text-xs text-slate-600">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const WorkflowRow = ({ wf }) => {
  const total = wf.enriched + wf.no_website + wf.already_contacted;
  const successRate = total > 0 ? Math.round((wf.websites_found / total) * 100) : 0;
  return (
    <tr className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors" data-testid={`workflow-row-${wf.name}`}>
      <td className="py-3 px-4">
        <span className="text-sm text-white font-medium">{wf.name}</span>
      </td>
      <td className="py-3 px-4" data-label="Status">
        <Badge variant="outline" className={
          wf.status === 'completed' ? 'border-emerald-600 text-emerald-400' :
          wf.status === 'processing' ? 'border-amber-600 text-amber-400' :
          wf.status === 'failed' ? 'border-red-600 text-red-400' :
          'border-slate-600 text-slate-400'
        }>{wf.status}</Badge>
      </td>
      <td className="py-3 px-4 text-sm text-slate-300 font-mono" data-label="Checked">{wf.enriched}</td>
      <td className="py-3 px-4 text-sm text-blue-400 font-mono" data-label="Websites">{wf.websites_found}</td>
      <td className="py-3 px-4 text-sm text-violet-400 font-mono" data-label="Emails">{wf.emails_generated}</td>
      <td className="py-3 px-4 text-sm text-emerald-400 font-mono" data-label="Sent">{wf.emails_sent}</td>
      <td className="py-3 px-4 text-sm text-slate-400 font-mono" data-label="Hit rate">{successRate}%</td>
    </tr>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (e) {
    return iso.substring(0, 10);
  }
};

const PipelineSettingsCard = () => {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchPipelines = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pipelines/followup-settings`);
      setPipelines(res.data.pipelines || []);
    } catch (err) {
      toast.error('Failed to load pipelines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPipelines(); }, []);

  const handleToggle = async (pipelineId, enabled) => {
    setToggling(pipelineId);
    try {
      await axios.patch(`${API}/pipelines/${pipelineId}/followup-setting`, { enabled });
      setPipelines(prev => prev.map(p => p.id === pipelineId ? { ...p, auto_followups_enabled: enabled } : p));
      const pipeline = pipelines.find(p => p.id === pipelineId);
      toast.success(
        enabled
          ? `Auto follow-ups enabled for "${pipeline?.name}"`
          : `Auto follow-ups paused for "${pipeline?.name}"`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setToggling(null);
    }
  };

  const enabledCount = pipelines.filter(p => p.auto_followups_enabled).length;
  const disabledCount = pipelines.length - enabledCount;

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="pipeline-settings-card">
      <CardHeader
        className="pb-2 cursor-pointer hover:bg-slate-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid="pipeline-settings-header"
      >
        <CardTitle className="text-sm font-medium text-slate-300 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ZapIcon className="w-4 h-4 text-violet-400" />
            Pipelines Included in Auto Follow-ups
          </span>
          <span className="text-xs text-slate-500 font-normal">
            {loading ? '…' : (
              <>
                {enabledCount} included
                {disabledCount > 0 && <span className="text-amber-400"> · {disabledCount} paused</span>}
                <span className="ml-2 text-slate-600">{expanded ? '▲' : '▼'}</span>
              </>
            )}
          </span>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-3">
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
            </div>
          ) : pipelines.length === 0 ? (
            <p className="text-sm text-slate-500 italic text-center py-4">No pipelines yet.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 mb-3">
                Toggle off to stop the scheduler from automatically following up on emails sent from a specific pipeline. Affects future runs only - already-sent follow-ups stay in place.
              </p>
              <div className="divide-y divide-slate-800">
                {pipelines.map(p => (
                  <div key={p.id} className="flex items-center justify-between py-3" data-testid={`pipeline-setting-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{p.name}</p>
                      <p className="text-[11px] text-slate-500">
                        {p.lead_count} lead{p.lead_count === 1 ? '' : 's'} · {p.sent_count} email{p.sent_count === 1 ? '' : 's'} sent
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className={`text-xs ${p.auto_followups_enabled ? 'text-emerald-400' : 'text-slate-500'}`}>
                        {p.auto_followups_enabled ? 'On' : 'Off'}
                      </span>
                      <Switch
                        checked={p.auto_followups_enabled}
                        onCheckedChange={(checked) => handleToggle(p.id, checked)}
                        disabled={toggling === p.id}
                        data-testid={`switch-${p.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const FollowupsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/analytics/followups`);
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load follow-up analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="followups-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (!data) return null;

  const { totals, pending, uplift, daily_followups, recent } = data;
  const maxDaily = Math.max(...daily_followups.map(d => d.count), 1);
  const upliftPositive = uplift.uplift_pct > 0;

  return (
    <div className="space-y-6 mt-4" data-testid="followups-content">
      {/* Totals */}
      <div>
        <h2 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-violet-400" />
          Automated Follow-ups
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={Send} label="Total Sent" value={totals.total_followups_sent} color="text-violet-400" bgColor="bg-violet-500/10" />
          <StatCard icon={BarChart3} label="Last 30 Days" value={totals.followups_last_30d} color="text-blue-400" bgColor="bg-blue-500/10" />
          <StatCard icon={Mail} label="1st Follow-ups" value={totals.first_followups} color="text-amber-400" bgColor="bg-amber-500/10" />
          <StatCard icon={Mail} label="2nd Follow-ups" value={totals.second_followups} color="text-rose-400" bgColor="bg-rose-500/10" />
        </div>
      </div>

      {/* Pending queue */}
      <div>
        <h2 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-400" />
          Pending in Queue
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Clock} label="1st Due" value={pending.first_followup_due} sub="Sent 7+ days ago, no reply" color="text-amber-400" bgColor="bg-amber-500/10" />
          <StatCard icon={Clock} label="2nd Due" value={pending.second_followup_due} sub="Last follow-up 7+ days ago" color="text-rose-400" bgColor="bg-rose-500/10" />
          <StatCard icon={RefreshCw} label="Total Due" value={pending.total_due} sub="Will send next scheduler cycle" color="text-violet-400" bgColor="bg-violet-500/10" />
        </div>
      </div>

      {/* Per-Pipeline settings */}
      <PipelineSettingsCard />

      {/* Reply Uplift */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="reply-uplift">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            Reply Uplift from Follow-ups
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-slate-800/40 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">First email only</p>
              <p className="text-2xl font-mono text-white">{uplift.first_email_reply_rate}%</p>
              <p className="text-[11px] text-slate-600 mt-1">{uplift.replies_first_email_only} replies / {uplift.originals_without_followup} sent</p>
            </div>
            <div className="bg-slate-800/40 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">After follow-up</p>
              <p className="text-2xl font-mono text-emerald-400">{uplift.followup_reply_rate}%</p>
              <p className="text-[11px] text-slate-600 mt-1">{uplift.replies_after_followup} replies / {uplift.originals_with_followup} sent</p>
            </div>
            <div className={`rounded-lg p-4 ${upliftPositive ? 'bg-emerald-500/10 border border-emerald-700/40' : 'bg-slate-800/40'}`}>
              <p className="text-xs text-slate-500 mb-1">Uplift</p>
              <p className={`text-2xl font-mono flex items-center gap-1 ${upliftPositive ? 'text-emerald-400' : 'text-slate-400'}`}>
                {upliftPositive ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                {upliftPositive ? '+' : ''}{uplift.uplift_pct}pp
              </p>
              <p className="text-[11px] text-slate-600 mt-1">Percentage point change in reply rate</p>
            </div>
          </div>
          {uplift.originals_with_followup === 0 && (
            <p className="text-xs text-slate-500 italic">
              Not enough follow-up data yet. Uplift becomes meaningful once more emails reach the 7-day mark.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Daily follow-ups bar chart */}
      {daily_followups.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800" data-testid="daily-followups-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-violet-400" />
              Follow-ups Sent (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-[2px] h-32">
              {daily_followups.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
                  <div
                    className="w-full bg-violet-500/60 hover:bg-violet-400/80 rounded-t transition-colors min-h-[2px]"
                    style={{ height: `${(d.count / maxDaily) * 100}%` }}
                  />
                  <div className="hidden group-hover:block absolute -top-8 bg-slate-800 border border-slate-700 text-[10px] text-white px-2 py-1 rounded whitespace-nowrap z-10">
                    {d.date}: {d.count} sent
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-slate-600">{daily_followups[0]?.date}</span>
              <span className="text-[10px] text-slate-600">{daily_followups[daily_followups.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent follow-ups */}
      {recent.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800" data-testid="recent-followups">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Mail className="w-4 h-4 text-violet-400" />
              Recent Follow-ups
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full stack-mobile">
                <thead>
                  <tr className="border-b border-slate-700 text-xs text-slate-500 uppercase tracking-wider">
                    <th className="text-left py-2 px-4 font-medium">Sent</th>
                    <th className="text-left py-2 px-4 font-medium">Contact</th>
                    <th className="text-left py-2 px-4 font-medium">Company</th>
                    <th className="text-left py-2 px-4 font-medium">Subject</th>
                    <th className="text-left py-2 px-4 font-medium">#</th>
                    <th className="text-left py-2 px-4 font-medium">Replied?</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r, i) => (
                    <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors" data-testid={`followup-row-${i}`}>
                      <td className="py-3 px-4 text-sm text-slate-400 font-mono whitespace-nowrap" data-label="Sent">{formatDate(r.sent_at)}</td>
                      <td className="py-3 px-4 text-sm text-slate-300" data-label="Contact">{r.lead_contact || r.lead_email}</td>
                      <td className="py-3 px-4 text-sm text-white" data-label="Company">{r.lead_company || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-300 max-w-xs truncate" data-label="Subject">{r.subject}</td>
                      <td className="py-3 px-4" data-label="Stage">
                        <Badge variant="outline" className={r.followup_number === 2 ? 'border-rose-600 text-rose-400' : 'border-amber-600 text-amber-400'}>
                          #{r.followup_number}
                        </Badge>
                      </td>
                      <td className="py-3 px-4" data-label="Replied">
                        {r.replied ? (
                          <Badge variant="outline" className="border-emerald-600 text-emerald-400">Replied</Badge>
                        ) : (
                          <span className="text-xs text-slate-600">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {recent.length === 0 && totals.total_followups_sent === 0 && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-12 text-center">
            <RefreshCw className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No automated follow-ups have been sent yet.</p>
            <p className="text-slate-600 text-xs mt-1">Follow-ups are sent automatically 7 days after the original email if there's no reply.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const DueNowTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(null); // email_id being previewed
  const [previewData, setPreviewData] = useState(null); // {row, draft}
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [sendingEdit, setSendingEdit] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/followups/due`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load due follow-ups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSendAll = async () => {
    if (!window.confirm(
      `Generate and send ${data.total} follow-up${data.total === 1 ? '' : 's'} right now? They will be sent directly from your email - no approval queue.`
    )) return;

    setSending(true);
    try {
      const res = await axios.post(`${API}/followups/send-all-due`);
      toast.success(res.data.message || `Queued ${res.data.queued} follow-ups`, { duration: 6000 });
      setTimeout(fetchData, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send follow-ups');
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async (row) => {
    setPreviewLoading(row.id);
    try {
      const res = await axios.post(`${API}/followups/preview/${row.id}`);
      setPreviewData({ row, draft: res.data });
      setEditSubject(res.data.subject || '');
      setEditBody(res.data.body || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setPreviewLoading(null);
    }
  };

  const handleEditSend = async () => {
    if (!previewData || !editBody.trim()) {
      toast.error('Body cannot be empty');
      return;
    }
    if (!window.confirm(`Send this follow-up to ${previewData.draft.to} now?`)) return;

    setSendingEdit(true);
    try {
      const res = await axios.post(`${API}/followups/send/${previewData.row.id}`, {
        subject: editSubject,
        body: editBody,
      });
      toast.success(res.data.message || 'Follow-up sent', { duration: 5000 });
      setPreviewData(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send follow-up');
    } finally {
      setSendingEdit(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="due-now-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }
  if (!data) return null;

  const { due, total, first_stage_count, second_stage_count } = data;

  return (
    <div className="space-y-6 mt-4" data-testid="due-now-content">
      {/* Header card with action */}
      <Card className="bg-gradient-to-br from-violet-950/40 to-slate-900/50 border-violet-800/40">
        <CardContent className="py-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-violet-500/10 rounded-lg">
                <ZapIcon className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <p className="text-2xl font-mono text-white">{total} due</p>
                <p className="text-xs text-slate-400">
                  {first_stage_count} first follow-up{first_stage_count === 1 ? '' : 's'} · {second_stage_count} second follow-up{second_stage_count === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            <Button
              onClick={handleSendAll}
              disabled={sending || total === 0}
              className="bg-violet-600 hover:bg-violet-500 text-white"
              data-testid="send-all-due-btn"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Queuing…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send All Due Now</>
              )}
            </Button>
          </div>
          {total > 50 && (
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-400">
              <AlertCircle className="w-4 h-4" />
              <span>Sends are capped at 50 per click to stay within rate limits. Click again after a few minutes for the rest.</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      {total === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-slate-300 text-sm">All caught up - no follow-ups due right now.</p>
            <p className="text-slate-600 text-xs mt-1">Eligible emails appear here 7 days after they're sent (and 7 days after the first follow-up, if applicable).</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-slate-900/50 border-slate-800" data-testid="due-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              Follow-ups Due Now
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full stack-mobile">
                <thead>
                  <tr className="border-b border-slate-700 text-xs text-slate-500 uppercase tracking-wider">
                    <th className="text-left py-2 px-4 font-medium">Stage</th>
                    <th className="text-left py-2 px-4 font-medium">Contact</th>
                    <th className="text-left py-2 px-4 font-medium">Company</th>
                    <th className="text-left py-2 px-4 font-medium">Original Subject</th>
                    <th className="text-left py-2 px-4 font-medium">Waiting</th>
                    <th className="text-right py-2 px-4 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {due.map((d, i) => (
                    <tr key={d.id || i} className="border-b border-slate-800 hover:bg-slate-800/30 transition-colors" data-testid={`due-row-${i}`}>
                      <td className="py-3 px-4" data-label="Stage">
                        <Badge variant="outline" className={d.followup_stage === 2 ? 'border-rose-600 text-rose-400' : 'border-amber-600 text-amber-400'}>
                          #{d.followup_stage} {d.followup_stage === 2 ? '(final)' : ''}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm" data-label="Contact">
                        <div className="text-white">{d.lead_contact || '-'}</div>
                        <div className="text-xs text-slate-500">{d.lead_email}</div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-300" data-label="Company">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-slate-500" />
                          {d.lead_company || '-'}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-400 max-w-xs truncate" data-label="Original subject">{d.subject}</td>
                      <td className="py-3 px-4 text-sm font-mono text-slate-300" data-label="Waiting">
                        {d.days_waiting != null ? `${d.days_waiting}d` : '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreview(d)}
                          disabled={previewLoading === d.id}
                          className="text-violet-400 hover:text-violet-300 hover:bg-violet-900/20"
                          data-testid={`preview-btn-${i}`}
                        >
                          {previewLoading === d.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewData} onOpenChange={(open) => !open && setPreviewData(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="followup-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-violet-400" />
              Follow-up Preview
              {previewData && (
                <Badge variant="outline" className={previewData.draft.followup_number === 2 ? 'border-rose-600 text-rose-400 ml-2' : 'border-amber-600 text-amber-400 ml-2'}>
                  #{previewData.draft.followup_number} {previewData.draft.followup_number === 2 ? '(final)' : ''}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {previewData && (
            <div className="space-y-4">
              {/* Headers */}
              <div className="space-y-3 text-sm border border-slate-800 rounded-lg p-4 bg-slate-900/50">
                <div className="flex gap-3 items-start">
                  <span className="text-slate-500 w-16 pt-1.5 text-xs">From:</span>
                  <span className="text-slate-200 font-mono text-xs pt-1.5">{previewData.draft.from}</span>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-slate-500 w-16 pt-1.5 text-xs">To:</span>
                  <span className="text-slate-200 font-mono text-xs pt-1.5">{previewData.draft.to}</span>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-slate-500 w-16 pt-2 text-xs">Subject:</span>
                  <Input
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    className="bg-slate-950 border-slate-800 text-white flex-1"
                    placeholder="Re: ..."
                    data-testid="preview-subject-input"
                  />
                </div>
              </div>

              {/* Editable body */}
              <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Body (editable)</span>
                  <span className="text-[10px] text-slate-600">{editBody.length} chars</span>
                </div>
                <Textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={8}
                  className="bg-slate-950 border-slate-800 text-slate-100 text-sm font-sans resize-y"
                  data-testid="preview-body-textarea"
                />
              </div>

              {/* Quoted original preview */}
              <details className="border border-slate-800 rounded-lg bg-slate-900/30">
                <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200 px-4 py-2 select-none">
                  Show quoted original email (appears below your message in the recipient's inbox)
                </summary>
                <div className="px-4 pb-4 pt-2 border-t border-slate-800">
                  <div className="text-xs text-slate-500 mb-2">
                    Original sent {previewData.draft.original_sent_at ? new Date(previewData.draft.original_sent_at).toLocaleDateString() : ''} - Subject: <span className="text-slate-300">{previewData.draft.original_subject}</span>
                  </div>
                  <div className="text-xs text-slate-400 whitespace-pre-wrap border-l-2 border-slate-700 pl-3">
                    {previewData.draft.original_body || '(no body)'}
                  </div>
                </div>
              </details>

              <p className="text-xs text-slate-500 italic">
                Your edited message will be sent immediately with "Re: ..." threading. The original email will be auto-quoted below.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPreviewData(null)} disabled={sendingEdit} className="bg-slate-900 border-slate-700 hover:bg-slate-800" data-testid="preview-close-btn">
              Cancel
            </Button>
            <Button onClick={handleEditSend} disabled={sendingEdit || !editBody.trim()} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="preview-send-btn">
              {sendingEdit ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Edit &amp; Send</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const fmtNum = (n) => (n == null ? '0' : Number(n).toLocaleString());
const fmtDate = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString(undefined,{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}); } catch(e){ return String(iso).slice(0,16);} };

const TrendBars = ({ trend }) => {
  const max = Math.max(...trend.map((d) => d.visitors), 1);
  const n = trend.length;
  const step = Math.max(1, Math.ceil(n / 12)); // avoid label clutter on 30/90-day ranges
  const peakIdx = trend.reduce((best, d, i) => (d.visitors > trend[best].visitors ? i : best), 0);
  const peak = trend[peakIdx] || { visitors: 0, day: '' };
  return (
    <div className="flex gap-2" data-testid="traffic-trend-wrap">
      {/* y-axis scale */}
      <div className="flex flex-col justify-between h-32 text-[9px] text-slate-500 font-mono text-right pr-1 shrink-0 w-10">
        <span>{max.toLocaleString()}</span>
        <span>{Math.round(max / 2).toLocaleString()}</span>
        <span>0</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-end gap-[3px] h-32 border-l border-b border-slate-800 pl-1 relative" data-testid="traffic-trend">
          {/* faint mid-value gridline */}
          <div className="absolute left-0 right-0 top-1/2 border-t border-dashed border-slate-700/50 pointer-events-none" data-testid="trend-midline" />
          {trend.map((d, i) => (
            <div key={i} className="flex-1 h-full flex flex-col justify-end items-center group relative min-w-0">
              {i === peakIdx && peak.visitors > 0 && (
                <span className="absolute -top-4 text-[9px] font-mono text-emerald-400 whitespace-nowrap z-10" data-testid="trend-peak-label">{peak.visitors.toLocaleString()}</span>
              )}
              <div className={`w-full rounded-t transition-colors min-h-[2px] ${i === peakIdx ? 'bg-emerald-400' : 'bg-emerald-500/70 hover:bg-emerald-400'}`} style={{ height: `${Math.round((d.visitors / max) * 100)}%` }} />
              <div className="hidden group-hover:block absolute -top-9 bg-slate-800 border border-slate-700 text-[10px] text-white px-2 py-1 rounded whitespace-nowrap z-20">{d.day}: {d.visitors.toLocaleString()} visitors · {d.pageviews.toLocaleString()} views</div>
            </div>
          ))}
        </div>
        {/* date labels aligned under bars */}
        <div className="flex gap-[3px] pl-1 mt-1">
          {trend.map((d, i) => (
            <div key={i} className="flex-1 text-center min-w-0">
              {(i % step === 0 || i === n - 1) && (
                <span className="text-[9px] text-slate-600 whitespace-nowrap">{String(d.day).slice(5)}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, color, title, children }) => (
  <section>
    <h2 className="text-lg font-medium text-white mb-3 flex items-center gap-2"><Icon className={`w-4 h-4 ${color}`} />{title}</h2>
    {children}
  </section>
);

const BreakdownCard = ({ title, icon: Icon, rows, nameKey, valueKey, unit, empty }) => {
  const data = rows || [];
  const max = Math.max(...data.map((r) => r[valueKey] || 0), 1);
  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid={`breakdown-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}{title}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {data.length ? data.map((r, i) => (
          <div key={i}>
            <div className="flex justify-between text-sm mb-0.5">
              <span className="text-slate-300 truncate mr-3">{r[nameKey] || '-'}</span>
              <span className="text-white font-mono whitespace-nowrap">{fmtNum(r[valueKey])} <span className="text-slate-600">{unit}</span></span>
            </div>
            <div className="h-1 rounded bg-slate-800"><div className="h-1 rounded bg-emerald-500/60" style={{ width: `${((r[valueKey] || 0) / max) * 100}%` }} /></div>
          </div>
        )) : <p className="text-sm text-slate-500">{empty || 'No data yet.'}</p>}
      </CardContent>
    </Card>
  );
};


const OverviewTab = ({ summary, days, setDays, refreshing }) => {
  const [drill, setDrill] = useState(null);
  if (!summary) return (
    <div className="flex items-center justify-center h-48" data-testid="overview-empty"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
  );
  const e = summary.email, a = summary.audience, l = summary.leads, f = summary.funnel || [];
  const openDrill = (kind, title) => setDrill({ kind, title });
  const w = summary.website || {}; const fbs = summary.funnel_by_source || [];
  const label = `last ${days} days`;
  return (
    <div className="space-y-8 mt-4" data-testid="overview-content">
      <div className="flex items-center gap-2" data-testid="range-toggle">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${days === d ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:border-emerald-500'}`}
            data-testid={`range-${d}`}>{d} days</button>
        ))}
        {refreshing && <Loader2 className="w-4 h-4 animate-spin text-emerald-400 ml-1" data-testid="range-refreshing" />}
      </div>
      {/* WEBSITE */}
      <Section icon={TrendingUp} color="text-emerald-400" title="Website traffic">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Live now" value={fmtNum(w.live)} sub="active in last 5 min" color="text-emerald-400" bgColor="bg-emerald-500/10" />
          <StatCard icon={Eye} label="Unique visitors" value={fmtNum(w.visitors)} sub={label} color="text-blue-400" bgColor="bg-blue-500/10" />
          <StatCard icon={Layers} label="Sessions" value={fmtNum(w.sessions)} sub={label} color="text-cyan-400" bgColor="bg-cyan-500/10" />
          <StatCard icon={BarChart3} label="Page views" value={fmtNum(w.views)} sub={label} color="text-violet-400" bgColor="bg-violet-500/10" />
          <StatCard icon={UserPlus} label="New visitors" value={fmtNum(w.new_visitors)} sub="first-ever visit" color="text-emerald-400" bgColor="bg-emerald-500/10" />
          <StatCard icon={Repeat} label="Returning" value={fmtNum(w.returning_visitors)} sub="seen before" color="text-amber-400" bgColor="bg-amber-500/10" />
          <StatCard icon={Eye} label="Pages / visit" value={w.avg_pages ?? 0} sub="avg this period" color="text-pink-400" bgColor="bg-pink-500/10" />
          <StatCard icon={Send} label="Emails sent" value={fmtNum(e.emails_sent)} sub={label} color="text-slate-400" bgColor="bg-slate-500/10" />
        </div>
        <Card className="bg-slate-900/50 border-slate-800 mt-4"><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-400" />Visitors - {label}</CardTitle></CardHeader>
          <CardContent>{w.trend?.length ? <TrendBars trend={w.trend} /> : <p className="text-sm text-slate-500">No traffic recorded yet.</p>}</CardContent></Card>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <BreakdownCard title="Traffic sources" rows={w.sources} nameKey="source" valueKey="visitors" unit="visitors" />
          <BreakdownCard title="Top referrer domains" icon={ExternalLink} rows={w.referrers} nameKey="domain" valueKey="visitors" unit="visitors" empty="No external referrers yet." />
          <BreakdownCard title="Top pages" rows={w.top_pages} nameKey="path" valueKey="views" unit="views" />
          <BreakdownCard title="Top landing pages" rows={w.landing_pages} nameKey="path" valueKey="sessions" unit="sessions" />
          <BreakdownCard title="Devices" icon={Smartphone} rows={w.devices} nameKey="name" valueKey="visitors" unit="visitors" />
          <BreakdownCard title="Browsers" rows={w.browsers} nameKey="name" valueKey="visitors" unit="visitors" />
          <BreakdownCard title="Countries" icon={Globe} rows={w.countries} nameKey="name" valueKey="visitors" unit="visitors" empty="No geography captured yet (builds from now on)." />
        </div>
      </Section>

      {/* CALLS */}
      <Section icon={Users} color="text-[#00A79D]" title="Calls">
        <CallStatsPanel days={days} />
      </Section>

      {/* EMAIL */}
      <Section icon={Send} color="text-blue-400" title="Email performance">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <StatCard icon={Send} label="Emails sent" value={fmtNum(e.emails_sent)} sub={`${e.campaigns_sent} sent · ${e.campaigns_scheduled} scheduled`} color="text-blue-400" bgColor="bg-blue-500/10" />
          <StatCard icon={Eye} label="Opened" value={fmtNum(e.opens_unique)} sub={`${e.open_rate}% open rate`} color="text-emerald-400" bgColor="bg-emerald-500/10" />
          <StatCard icon={MousePointer} label="Clicked" value={fmtNum(e.clicks_unique)} sub={`${e.click_rate}% click rate`} color="text-violet-400" bgColor="bg-violet-500/10" />
          <StatCard icon={AlertCircle} label="Bounced" value={fmtNum(e.bounces)} sub="auto-suppressed" color="text-red-400" bgColor="bg-red-500/10" />
          <StatCard icon={Clock} label="Scheduled" value={fmtNum(e.campaigns_scheduled)} sub={`${e.campaigns_draft} drafts`} color="text-amber-400" bgColor="bg-amber-500/10" />
        </div>
        <Card className="bg-slate-900/50 border-slate-800 mt-4" data-testid="recent-campaigns">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-300">Recent campaigns</CardTitle></CardHeader>
          <CardContent className="p-0">
            {(e.recent || []).length ? (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b border-slate-800 text-xs text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-2 px-4 font-medium">Campaign</th>
                  <th className="text-right py-2 px-3 font-medium">Sent</th>
                  <th className="text-right py-2 px-3 font-medium">Opened</th>
                  <th className="text-right py-2 px-3 font-medium">Clicked</th>
                  <th className="text-left py-2 px-3 font-medium">Status</th>
                </tr></thead>
                <tbody>{e.recent.map((c) => (
                  <tr key={c.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                    <td className="py-2.5 px-4"><div className="text-white font-medium truncate max-w-[240px]">{c.name}</div></td>
                    <td className="py-2.5 px-3 text-right text-slate-300 font-mono">{fmtNum(c.sent)}</td>
                    <td className="py-2.5 px-3 text-right text-emerald-400 font-mono">{fmtNum(c.opens)} <span className="text-slate-600 text-xs">({c.open_rate}%)</span></td>
                    <td className="py-2.5 px-3 text-right text-violet-400 font-mono">{fmtNum(c.clicks)} <span className="text-slate-600 text-xs">({c.click_rate}%)</span></td>
                    <td className="py-2.5 px-3"><Badge variant="outline" className={c.status === 'sent' ? 'border-emerald-600 text-emerald-400' : 'border-amber-600 text-amber-400'}>{c.status}</Badge></td>
                  </tr>
                ))}</tbody>
              </table></div>
            ) : <p className="text-sm text-slate-500 px-4 py-6">No campaigns sent yet.</p>}
          </CardContent>
        </Card>
        {(e.upcoming || []).length > 0 && (
          <Card className="bg-slate-900/50 border-slate-800 mt-4">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" />Scheduled to send</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {e.upcoming.map((c) => (
                <div key={c.id} className="flex justify-between text-sm"><span className="text-slate-300 truncate mr-3">{c.name}</span><span className="text-amber-400 font-mono">{fmtDate(c.scheduled_at)}</span></div>
              ))}
            </CardContent>
          </Card>
        )}
      </Section>

      {/* AUDIENCE */}
      <Section icon={Users} color="text-violet-400" title="Audience">
        <p className="text-xs text-slate-500 -mt-1 mb-2">Click any stat to see and open the records behind it.</p>
        <div className="mb-3">
          <StatCard icon={PoundSterling} label="Confirmed-package revenue"
            value={'£' + Number(a.confirmed_revenue || 0).toLocaleString()}
            sub={`recurring ARR from ${fmtNum(a.confirmed_suppliers)} confirmed suppliers`}
            color="text-amber-400" bgColor="bg-amber-500/10"
            onClick={() => openDrill('confirmed_suppliers', 'Confirmed suppliers')} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={Users} label="Contacts" value={fmtNum(a.contacts)} color="text-blue-400" bgColor="bg-blue-500/10" onClick={() => openDrill('contacts', 'Contacts')} />
          <StatCard icon={Building2} label="Suppliers" value={fmtNum(a.suppliers)} color="text-violet-400" bgColor="bg-violet-500/10" onClick={() => openDrill('suppliers', 'Suppliers')} />
          <StatCard icon={CheckCircle} label="Confirmed suppliers" value={fmtNum(a.confirmed_suppliers)} sub="on paid 'Confirmed' tier" color="text-emerald-400" bgColor="bg-emerald-500/10" onClick={() => openDrill('confirmed_suppliers', 'Confirmed suppliers')} />
          <StatCard icon={Building2} label="Providers" value={fmtNum(a.providers)} color="text-cyan-400" bgColor="bg-cyan-500/10" onClick={() => openDrill('providers', 'Providers')} />
          <StatCard icon={TrendingUp} label="Engaged" value={fmtNum(a.engaged)} sub="opened/clicked" color="text-emerald-400" bgColor="bg-emerald-500/10" onClick={() => openDrill('engaged', 'Engaged contacts')} />
          <StatCard icon={CheckCircle} label="Claimed" value={fmtNum(a.claimed_suppliers)} color="text-amber-400" bgColor="bg-amber-500/10" onClick={() => openDrill('claimed_suppliers', 'Claimed suppliers')} />
          <StatCard icon={AlertCircle} label="Unsubscribed" value={fmtNum(a.unsubscribed)} color="text-slate-400" bgColor="bg-slate-500/10" onClick={() => openDrill('unsubscribed', 'Unsubscribed')} />
        </div>
      </Section>

      {/* LEADS */}
      <Section icon={Mail} color="text-amber-400" title="Leads & funnel">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Webinar sign-ups" value={fmtNum(l.webinar_registrations)} color="text-blue-400" bgColor="bg-blue-500/10" onClick={() => openDrill('webinar_registrations', 'Webinar sign-ups')} />
          <StatCard icon={Mail} label="Buying-help sign-ups" value={fmtNum(l.buying_help_signups)} color="text-emerald-400" bgColor="bg-emerald-500/10" onClick={() => openDrill('buying_help_signups', 'Buying-help sign-ups')} />
          <StatCard icon={CheckCircle} label="Assessments done" value={fmtNum(l.assessments_completed)} color="text-violet-400" bgColor="bg-violet-500/10" onClick={() => openDrill('assessments', 'Assessments completed')} />
          <StatCard icon={TrendingUp} label="New leads (30d)" value={fmtNum(l.leads_30d)} color="text-amber-400" bgColor="bg-amber-500/10" />
        </div>
      </Section>

      {/* FUNNEL */}
      <Section icon={TrendingUp} color="text-cyan-400" title="Conversion funnel (buying help)">
        <Card className="bg-slate-900/50 border-slate-800" data-testid="conversion-funnel">
          <CardContent className="pt-5 space-y-4">
            {f.length ? f.map((s, i) => {
              const top = f[0]?.value || 0;
              const prev = i > 0 ? (f[i - 1].value || 0) : null;
              const convPrev = prev ? Math.round((s.value / prev) * 100) : null;
              const width = top ? Math.max((s.value / top) * 100, s.value ? 4 : 0) : 0;
              return (
                <div key={s.stage}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">{s.stage}</span>
                    <span className="text-white font-mono">{fmtNum(s.value)}
                      {i > 0 && convPrev != null && <span className="text-slate-500 text-xs ml-2">{convPrev}% of previous</span>}</span>
                  </div>
                  <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 rounded-full transition-all" style={{ width: `${width}%` }} />
                  </div>
                </div>
              );
            }) : <p className="text-sm text-slate-500">No funnel data yet.</p>}
          </CardContent>
        </Card>
        {fbs.length > 0 && (
          <Card className="bg-slate-900/50 border-slate-800 mt-4" data-testid="funnel-by-source">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-300">Funnel by traffic source</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b border-slate-800 text-xs text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-2 px-4 font-medium">Source</th>
                  <th className="text-right py-2 px-3 font-medium">Visits</th>
                  <th className="text-right py-2 px-3 font-medium">Sign-ups</th>
                  <th className="text-right py-2 px-3 font-medium">Assessments</th>
                  <th className="text-right py-2 px-4 font-medium">Conv.</th>
                </tr></thead>
                <tbody>{fbs.map((r) => (
                  <tr key={r.source} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                    <td className="py-2.5 px-4 text-white">{r.source}</td>
                    <td className="py-2.5 px-3 text-right text-slate-300 font-mono">{fmtNum(r.visits)}</td>
                    <td className="py-2.5 px-3 text-right text-emerald-400 font-mono">{fmtNum(r.signups)}</td>
                    <td className="py-2.5 px-3 text-right text-violet-400 font-mono">{fmtNum(r.assessments)}</td>
                    <td className="py-2.5 px-4 text-right text-cyan-400 font-mono">{r.conv}%</td>
                  </tr>
                ))}</tbody>
              </table></div>
            </CardContent>
          </Card>
        )}
      </Section>
      <DrilldownDialog drill={drill} days={days} onClose={() => setDrill(null)} />
    </div>
  );
};

const reasonBadge = (r) => {
  const map = { unsubscribed: 'border-slate-600 text-slate-300', bounced: 'border-red-600 text-red-400', complained: 'border-amber-600 text-amber-400' };
  return <Badge key={r} variant="outline" className={`${map[r] || 'border-slate-600 text-slate-300'} mr-1 capitalize`}>{r}</Badge>;
};

const SuppressionTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bulk-email/suppression`, { params: { search, limit: 200 } });
      setData(res.data);
    } catch { toast.error('Failed to load suppression list'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const reinclude = async (email) => {
    setBusy(email);
    try {
      await axios.post(`${API}/admin/bulk-email/suppression/reinclude`, { email });
      toast.success(`${email} re-included - they can be emailed again`);
      load();
    } catch { toast.error('Could not re-include'); }
    finally { setBusy(null); }
  };

  const c = data?.counts;
  return (
    <div className="space-y-4 mt-4" data-testid="suppression-content">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={AlertCircle} label="Suppressed" value={c?.total ?? ' - '} color="text-red-400" bgColor="bg-red-500/10" />
        <StatCard icon={AlertCircle} label="Unsubscribed" value={c?.unsubscribed ?? ' - '} color="text-slate-400" bgColor="bg-slate-500/10" />
        <StatCard icon={AlertCircle} label="Bounced" value={c?.bounced ?? ' - '} color="text-red-400" bgColor="bg-red-500/10" />
        <StatCard icon={AlertCircle} label="Complaints" value={c?.complained ?? ' - '} color="text-amber-400" bgColor="bg-amber-500/10" />
      </div>
      <div className="flex gap-2">
        <Input placeholder="Search email, name or company" value={search}
          onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
          className="bg-slate-900/50 border-slate-800 text-white h-9" data-testid="suppression-search" />
        <Button size="sm" onClick={load} data-testid="suppression-search-btn">Search</Button>
      </div>
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500 inline" /></div>
          ) : data?.rows?.length ? (
            <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="suppression-table">
              <thead><tr className="border-b border-slate-800 text-xs text-slate-500 uppercase tracking-wider">
                <th className="text-left py-2 px-4 font-medium">Contact</th>
                <th className="text-left py-2 px-3 font-medium">Reason</th>
                <th className="text-right py-2 px-4 font-medium">Action</th>
              </tr></thead>
              <tbody>{data.rows.map((r) => (
                <tr key={r.email} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid="suppression-row">
                  <td className="py-2.5 px-4">
                    <div className="text-white font-medium">{r.name || r.email}</div>
                    {(r.name || r.company) && <div className="text-xs text-slate-500">{r.email}{r.company ? ` · ${r.company}` : ''}</div>}
                  </td>
                  <td className="py-2.5 px-3">{r.reasons.map(reasonBadge)}</td>
                  <td className="py-2.5 px-4 text-right">
                    <Button size="sm" variant="outline" disabled={busy === r.email}
                      onClick={() => reinclude(r.email)} data-testid={`suppression-reinclude-${r.email}`}>
                      {busy === r.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" />Re-include</>}
                    </Button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          ) : (
            <p className="py-10 text-center text-sm text-slate-500" data-testid="suppression-empty">
              No suppressed contacts - nobody has unsubscribed, bounced or complained yet.
            </p>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-slate-500">Suppressed contacts are automatically excluded from every send. Re-include only if you're sure the address is valid and they want your emails.</p>
    </div>
  );
};

const Analytics = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let active = true;
    const load = async (silent) => {
      if (!silent) setRefreshing(true);
      try {
        const m = await axios.get(`${API}/analytics/email-summary`, { params: { days } });
        if (!active) return;
        setSummary(m.data);
      } catch (err) {
        if (!silent) toast.error('Failed to load analytics');
      } finally {
        if (active) { setLoading(false); setRefreshing(false); }
      }
    };
    load(false);
    const id = setInterval(() => load(true), 30000);
    return () => { active = false; clearInterval(id); };
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2">Analytics</h1>
          <p className="text-slate-400">
            Website traffic, email performance, audience and leads at a glance
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full" data-testid="analytics-live-badge">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live · updates every 30s
        </span>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-slate-900/50 border border-slate-800">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
            Overview
          </TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
            Follow-ups
          </TabsTrigger>
          <TabsTrigger value="due-now" data-testid="tab-due-now" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
            Due Now
          </TabsTrigger>
          <TabsTrigger value="suppression" data-testid="tab-suppression" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white">
            Suppression
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <VisitorSegmentsCard />
          <CareSettingsCard />
          <OverviewTab summary={summary} days={days} setDays={setDays} refreshing={refreshing} />
        </TabsContent>
        <TabsContent value="followups">
          <FollowupsTab />
        </TabsContent>
        <TabsContent value="due-now">
          <DueNowTab />
        </TabsContent>
        <TabsContent value="suppression">
          <SuppressionTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
